import {
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../core/services/logger.service';

/**
 * Represents a field definition in the form builder
 * Used for all field types (text, textarea, number, checkbox, etc.)
 */
export interface FieldDefinition {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  options?: {
    label: string;
    value: string;
    description?: string;
    isDefault?: boolean;
    metricAmount?: number;
  }[];
  /** Controls where options come from: inline array or a shared lookup list */
  optionsSource?: 'inline' | 'lookup-list';
  /** When optionsSource === 'lookup-list', the ID of the referenced LookupList */
  lookupListId?: string;
  allowMultiple?: boolean;
  allowMultiplePhotos?: boolean;
  filenamePrefix?: string;
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
  content?: string;
  fontSize?: string;
  fontWeight?: string;
  color: string;
  textAlign?: string;
  tag?: string;
  /** Per-field pre-fill override. 'default'/undefined → use form-level default. */
  prefillLastValue?: 'default' | 'always' | 'never';
  calculationKind?:
    | 'date_diff_between_fields'
    | 'date_diff_from_previous'
    | 'arithmetic_two_fields'
    | 'checklist_total_metric'
    | 'state_transition';
  inputFieldAId?: string;
  inputFieldBId?: string;
  currentFieldId?: string;
  checklistFieldId?: string;
  sourceFieldId?: string;
  outputFieldId?: string;
  transitions?: Array<{ from?: string[] | string; to?: string; label?: string; value?: string }>;
  transitionFallback?: string;
  arithmeticOperator?: 'add' | 'subtract' | 'multiply' | 'divide';
  numberFormat?: string;
  durationFormat?: string;
  emptyBehavior?: 'null' | 'zero';
  persistSnapshot?: true;
  durationInputUnit?: 'seconds' | 'minutes' | 'hours' | 'days';
  durationDisplayFormat?: 'short' | 'long';
  visibleWhen?: {
    fieldId: string;
    values: string[] | string;
    /** Temporary editor state: selected values when origin field has known options (multi-checkbox). Not persisted. */
    selectedValues?: string[];
  };
  visibleWhenMode?: 'none' | 'show_when_value';
}

/**
 * Base class for all field edit components in modal mode
 *
 * Handles common patterns:
 * - Receiving field data from parent via writable signal
 * - Providing save/cancel communication back to parent
 * - Managing field validation and state
 *
 * ## Signal-Based Modal Pattern
 *
 * In modal mode, the parent creates a writable signal and passes it to the editor.
 * The editor reads from and writes to this same signal throughout the editing session.
 * When save is clicked, the parent simply reads the modified field from the signal.
 *
 * Data flow:
 * 1. Parent: `const fieldSignal = signal({ ...originalField })` → creates writable signal
 * 2. Parent passes: `{ fieldSignal }` via componentInputs to editor
 * 3. Editor: User modifies field via template ↔ signal binding
 * 4. Editor: Updates fieldSignal via setFieldValue() or direct binding
 * 5. Parent save: Reads `const modifiedField = fieldSignal()`
 * 6. Parent: Updates fields array with modified field, closes modal
 *
 * This approach:
 * - No need for child component methods like getModifiedField()
 * - Direct access to modified field via parent's signal
 * - Single source of truth for field data
 * - Clean, reactive data flow
 *
 * Usage:
 * 1. Extend this class in your field-specific editor component
 * 2. Override saveChanges() for custom save logic (validation)
 * 3. Use this.field signal to access field data (writable)
 * 4. Use setFieldValue() to update field properties
 * 5. Let template bindings update the signal automatically
 *
 * @example
 * ```typescript
 * @Component({
 *   selector: 'app-field-text-edit',
 *   standalone: true,
 *   imports: [CommonModule, FormsModule, ...],
 *   templateUrl: './field-text-edit.component.html',
 *   styleUrl: './field-text-edit.component.css',
 * })
 * export class FieldTextEditComponent extends FieldEditBase {
 *   // Template: <input [(ngModel)]="field()!.label" />
 *   // This automatically updates the field signal (and parent's fieldSignal)
 *
 *   protected override saveChanges(): void {
 *     const field = this.field();
 *     if (!field) return;
 *
 *     // Validate required fields
 *     if (!field.label || !field.label.trim()) {
 *       this.setError('Label is required');
 *       return;  // Parent will keep modal open (saveDisabled stays set)
 *     }
 *
 *     // Field is valid
 *     // In modal mode: parent will close modal and read fieldSignal()
 *     // In routing mode: parent will navigate back after this method returns
 *     console.log('Saving text field:', field);
 *   }
 * }
 * ```
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '', // Don't instantiate directly; always extend
})
export abstract class FieldEditBase implements OnInit {
  protected log = inject(LoggerService).getLogger('field-edit-base');
  /**
   * Writable signal containing the field definition (preferred pattern for modal mode).
   * When provided by parent container, the editor updates this signal directly
   * instead of using the local field signal. This allows the parent to read the
   * modified field value directly from the signal when save is clicked.
   *
   * Usage pattern:
   * 1. Parent creates: const fieldSignal = signal({ ...field });
   * 2. Container passes to editor: componentRef.instance.fieldSignal = fieldSignal
   * 3. Editor receives fieldSignal via @Input setter
   * 4. Editor reads from and writes to fieldSignal (not local field signal)
   * 5. Parent reads: const modifiedField = fieldSignal();
   *
   * When not provided (routing mode), editor uses the local field signal instead.
   */
  @Input() set fieldSignal(signal: WritableSignal<FieldDefinition> | undefined) {
    if (signal) {
      this._fieldSignal = signal;
      // Initialize local field signal from the provided signal
      this.field.set(signal());
    }
  }

  private _fieldSignal: WritableSignal<FieldDefinition> | null = null;

  /**
   * Optional signal for controlling save button state and messaging (new signal-based pattern).
   * Set by parent (FieldEditContainerComponent in modal mode).
   *
   * Signal value:
   * - null: Button is enabled
   * - string: Button is disabled with this reason message
   *
   * Usage in modal mode:
   * Parent component creates this signal and passes it to the editor.
   * Editor updates it during save to prevent double-clicks or backend operations.
   *
   * In routing mode (parent not providing this signal):
   * - Save proceeds directly to navigation without signal coordination
   * - Component continues to work via navigation
   */
  @Input() saveDisabled: WritableSignal<string | null> | null = null;

  /**
   * Optional signal for controlling cancel button state and messaging (signal-based pattern).
   * Set by parent component in modal mode.
   *
   * Similar to saveDisabled for cancel operations.
   * Usually used if form is being saved to backend and shouldn't allow cancellation.
   */
  @Input() cancelDisabled: WritableSignal<string | null> | null = null;

  /**
   * Writable signal for field type change notifications (pure signal-based pattern).
   * Passed by parent FormBuilderComponent when opening field editor modal.
   *
   * When user selects a different field type from FieldTypeSelectorComponent,
   * onFieldTypeChanged() writes to this signal: typeChangeSignal.set({ newType, field })
   * Parent checks this signal in the save button handler and closes modal with 'typeChange' action.
   * Parent then recursively opens a new modal with the appropriate editor for the new type.
   *
   * Usage in parent save button:
   * ```typescript
   * const typeChange = typeChangeSignal();
   * if (typeChange) {
   *   modal.close({ action: 'typeChange', data: typeChange });
   *   return;
   * }
   * // Continue with normal save
   * ```
   */
  @Input() typeChangeSignal: WritableSignal<{
    newType: string;
    field: FieldDefinition;
  } | null> | null = null;

  /**
   * The current field being edited
   * Null while loading, contains field data once loaded
   *
   * ## Signal as Writable Data Container
   *
   * This signal serves as a writable container for field data.
   * In modal mode, it syncs with the parent's fieldSignal.
   * In routing mode, it's the canonical field source.
   *
   * Initialization:
   * - Modal mode: Initialized from parent's fieldSignal via @Input setter
   * - Routing mode: Initialized from FieldSyncService via loadFieldData()
   *
   * Updates:
   * - Modified by template via two-way binding: [(ngModel)]="field()!.label"
   * - Updated by setFieldValue() helper method (syncs to parent fieldSignal)
   * - Read by save() to validate and persist changes
   * - Read by parent directly from parent's fieldSignal in modal mode
   *
   * ## Mutation Pattern
   *
   * The field object inside the signal is mutable. Template bindings that modify
   * the field's properties will automatically update the signal via spread operator:
   *
   * ```typescript
   * // Template binding creates mutation (sync works automatically):
   * <input [(ngModel)]="field()!.label" /> → this.field()!.label = newValue
   *
   * // Helper method creates a single shared copy so Formly mutations (by-reference)
   * // are visible through both the local signal and the parent fieldSignal:
   * protected setFieldValue(key: K, value: V): void {
   *   const field = this.field();
   *   if (field) {
   *     field[key] = value;
   *     const newField = { ...field };    // ONE copy — shared by both signals
   *     this.field.set(newField);
   *     if (this._fieldSignal) {
   *       this._fieldSignal.set(newField); // SAME reference as local signal
   *     }
   *   }
   * }
   * ```
   *
   * Note: Template bindings (ngModel) handle sync automatically, but for programmatic
   * updates use setFieldValue() to ensure both signals stay in sync.
   */
  field = signal<FieldDefinition | null>(null);

  /**
   * Whether this is a new field (has not been saved before)
   * Used to customize UI (e.g., save button text)
   */
  isNew = signal<boolean>(false);

  /**
   * Whether a save operation is currently in progress
   * Used to disable save button and show loading states
   */
  isSaving = signal<boolean>(false);

  /**
   * Validation error message, if any
   * Use setError() and clearError() to manage this signal
   *
   * @example
   * ```typescript
   * if (!field.label) {
   *   this.setError('Label is required');
   *   return;
   * }
   * ```
   */
  error = signal<string | null>(null);

  /**
   * Computed signal: true if there's a validation error
   * Useful for disabling save button or showing error UI
   *
   * @example
   * ```html
   * <app-button [disabled]="hasError() || isSaving()">Save</app-button>
   * ```
   */
  hasError = computed<boolean>(() => this.error() !== null);

  /**
   * Initialize component
   * Called automatically by Angular; performs initial validation
   *
   * In modal mode:
   * - Field data is already loaded via fieldSignal @Input setter
   * - Runs initial validation via updateValidationState()
   * - Updates saveDisabled signal based on validation results
   * - As user edits, child components call updateValidationState() on input changes
   * - Editor writes to typeChangeSignal when user selects a new type
   * - Parent detects typeChangeSignal in the save button handler and closes with 'typeChange' action
   */
  ngOnInit(): void {
    this.loadFieldData();

    // Run initial validation
    // updateValidationState() checks if saveDisabled signal is provided
    if (this.saveDisabled) {
      this.updateValidationState();
    }

    // NOTE: No need to watch typeChangeSignal here
    // Editor simply writes to typeChangeSignal when user selects a new type
    // Parent's save button checks the signal and handles modal close
  }

  /**
   * Load field data from the provided fieldSignal
   * Called automatically in ngOnInit
   *
   * In modal mode, the fieldSignal is provided via @Input setter.
   * This method is primarily for initial setup and can be overridden
   * by subclasses for custom loading behavior.
   *
   * Note: The fieldSignal input setter handles initialization.
   * This method is retained for override compatibility.
   */
  protected loadFieldData(): void {
    // In modal mode, fieldSignal @Input setter handles initialization
    // Local field signal is already set from the provided signal
  }

  /**
   * Update validation state and sync to saveDisabled signal (modal mode only)
   *
   * USAGE PATTERN (Modal Mode):
   * Child components call this method on input/change events:
   * ```html
   * <input [(ngModel)]="field()!.label" (change)="updateValidationState()" />
   * ```
   *
   * This method:
   * 1. Clears previous validation error
   * 2. Calls saveChanges() for custom validation logic
   * 3. Updates the saveDisabled signal based on validation result:
   *    - null = validation passed, save button enabled
   *    - string = validation failed, save button disabled with error message
   *
   * Only updates the signal if running in modal mode (saveDisabled provided).
   * Route-based mode ignores this and uses save() method instead.
   */
  public updateValidationState(): void {
    // Only update signal in modal mode
    if (!this.saveDisabled) {
      return;
    }

    this.clearError();

    try {
      const field = this.field();
      if (!field) {
        this.setError('No field data to validate');
        this.saveDisabled.set('No field data to validate');
        return;
      }

      // Run custom validation logic (subclass override point)
      this.saveChanges();

      // Check results
      if (this.hasError()) {
        // Validation failed: disable button with error message
        this.log.debug('[FieldEditBase.updateValidationState] ❌ Validation error:', this.error());
        this.saveDisabled.set(this.error() || 'Validation failed');
      } else {
        // Validation passed: enable button
        this.log.debug(
          '[FieldEditBase.updateValidationState] ✅ Validation passed - ready to save',
        );
        this.saveDisabled.set(null);
      }
    } catch (err) {
      // Unexpected error during validation
      const errorMsg = String(err);
      this.setError(errorMsg);
      this.saveDisabled.set(errorMsg);
    }
  }

  /**
   * Save changes and communicate back to parent
   *
   * In modal mode (the only supported mode now):
   * - Parent checks saveDisabled signal to determine if save is ready
   * - Parent reads modified field via fieldSignal() when closing modal
   * - Validation happens continuously via updateValidationState() on user input
   * - saveDisabled signal is kept in sync automatically
   * - Parent button handler checks signal state (null = ready, string = error)
   *
   * This method handles the core save logic:
   * - Performs validation via saveChanges()
   * - Updates saveDisabled signal indicating ready/error state
   * - Parent handles modal closing based on signal state
   *
   * Type changes are now handled by parent's effect that watches typeChangeSignal.
   * This method is only responsible for field validation, not type changes.
   *
   * @example
   * ```html
   * <app-button
   *   variant="filled"
   *   (click)="save()"
   *   [disabled]="isSaving() || hasError()"
   *   data-testid="save-button"
   * >
   *   {{ 'formBuilder.saveField' | translate }}
   * </app-button>
   * ```
   */
  save(): void {
    this.log.debug('[FieldEditBase.save] ===== SAVE INITIATED ===== Field:', this.field()?.label);
    this.clearError();
    this.isSaving.set(true);

    // Indicate saving state
    if (this.saveDisabled) {
      this.saveDisabled.set('Saving...');
    }

    try {
      const field = this.field();
      if (!field) {
        this.setError('No field data to save');
        this.log.error('[FieldEditBase.save] ❌ NO FIELD DATA - cannot save');
        if (this.saveDisabled) {
          this.saveDisabled.set('No field data to save');
        }
        return;
      }

      this.log.debug(
        '[FieldEditBase.save] ✅ Field data exists. Starting saveChanges() validation...',
      );
      // Perform custom save logic in subclass
      // Subclass can call this.setError() to prevent modal from closing
      this.saveChanges();

      this.log.debug(
        '[FieldEditBase.save] saveChanges() completed. hasError:',
        this.hasError(),
        'Error message:',
        this.error(),
      );

      // Only proceed if no error was set during saveChanges()
      if (!this.hasError()) {
        this.log.debug('[FieldEditBase.save] ✅ Validation passed - ready to close modal');
        if (this.saveDisabled) {
          this.saveDisabled.set(null);
        }
      } else {
        // Validation error: set signal to indicate error
        this.log.warn(
          '[FieldEditBase.save] ❌ VALIDATION ERROR - setting saveDisabled with error message',
        );
        if (this.saveDisabled) {
          this.saveDisabled.set(this.error() || 'Validation failed');
        }
      }
    } finally {
      this.isSaving.set(false);
      this.log.debug('[FieldEditBase.save] ===== SAVE COMPLETED ====');
    }
  }

  /**
   * Abstract method: Subclass override point for custom save logic
   * Called by save() during validation
   *
   * Default implementation in base class does nothing
   * (field is already updated in signals via two-way binding)
   *
   * Override to:
   * - Validate field data (call this.setError() to abort save)
   * - Transform field data before saving
   * - Emit custom events
   *
   * @example
   * ```typescript
   * protected override saveChanges(): void {
   *   const field = this.field();
   *   if (!field) return;
   *
   *   // Validate required fields
   *   if (!field.label) {
   *     this.setError('Label is required');
   *     return;
   *   }
   *
   *   // Field is valid - save will proceed
   *   console.log('Saving field:', field);
   * }
   * ```
   *
   * @protected
   */
  protected saveChanges(): void {
    // Subclass implements custom save logic
    // Example: validate(), emit events, etc.
    // Default: do nothing (field already updated via two-way binding in signals)
  }

  /**
   * Cancel editing without saving changes
   * Call from template: `(click)="cancel()"`
   *
   * In modal mode (the only supported mode):
   * - Sets cancelDisabled signal to control button state
   * - Parent reads signal to determine when modal can be closed
   * - Parent handles closing the modal
   *
   * Changes are discarded (unless they were persisted to a service during editing)
   *
   * @example
   * ```html
   * <app-button
   *   variant="outlined"
   *   (click)="cancel()"
   *   data-testid="cancel-button"
   * >
   *   {{ 'formBuilder.cancel' | translate }}
   * </app-button>
   * ```
   */
  cancel(): void {
    this.log.debug(
      '[FieldEditBase.cancel] Signal-based modal mode - setting cancelDisabled to null (ready to close)',
    );
    if (this.cancelDisabled) {
      this.cancelDisabled.set(null);
    }
  }

  /**
   * Handle field type change via signal (pure signal-based pattern)
   * Called from field type selector component when user chooses a different field type
   * Preserves generic properties (label, required, etc.) while switching field types
   *
   * Usage from template:
   * ```html
   * <app-field-type-selector
   *   [field]="field()"
   *   (onFieldTypeSelected)="onFieldTypeChanged($event)"
   * />
   * ```
   *
   * Data flow:
   * 1. User selects new type from field type selector
   * 2. This method is called with the new type
   * 3. Field type is updated in local signal and parent's fieldSignal
   * 4. typeChangeSignal is updated: signal.set({ newType, field })
   * 5. Parent's save button detects signal change (non-null)
   * 6. Parent closes modal with action: 'typeChange'
   * 7. Parent reads updated field from fieldSignal
   * 8. Parent recursively opens new modal with new field type's editor
   *
   * @param newType The new field type selected by the user (e.g., 'email', 'textarea')
   *
   * @example
   * User is editing a text field and selects 'email' from the field type selector.
   * This method is called with newType='email', which:
   * 1. Updates the field type in the current field
   * 2. Writes to typeChangeSignal: { newType: 'email', field: {...} }
   * 3. Parent detects signal and closes modal with typeChange action
   * 4. Parent opens a new modal with the email field editor
   */
  onFieldTypeChanged(newType: string): void {
    this.log.debug('[FieldEditBase.onFieldTypeChanged] 🔵 ===== ENTRY ===== newType:', newType);

    const field = this.field();
    this.log.debug('[FieldEditBase.onFieldTypeChanged] 📊 Current field:', field);
    this.log.debug('[FieldEditBase.onFieldTypeChanged] 📊 Current field.type:', field?.type);

    if (!field) {
      this.log.debug('[FieldEditBase.onFieldTypeChanged] ⚠️ Early return - field is null');
      return;
    }

    this.log.debug(
      '[FieldEditBase.onFieldTypeChanged] 🔄 Switching field type from',
      field.type,
      'to',
      newType,
    );

    // Update field type (generic properties are preserved)
    field.type = newType;
    this.field.set({ ...field });
    this.log.debug(
      '[FieldEditBase.onFieldTypeChanged] ✅ Local field signal updated with new type:',
      this.field()?.type,
    );

    // Sync to parent's fieldSignal if provided (signal-based pattern)
    if (this._fieldSignal) {
      this.log.debug('[FieldEditBase.onFieldTypeChanged] 📡 Syncing to parent fieldSignal...');
      this._fieldSignal.set({ ...field });
      this.log.debug('[FieldEditBase.onFieldTypeChanged] ✅ Parent fieldSignal synced');
    } else {
      this.log.debug('[FieldEditBase.onFieldTypeChanged] ⚠️ No parent fieldSignal to sync');
    }

    // Write to typeChangeSignal to notify parent (pure signal-based pattern)
    this.log.debug(
      '[FieldEditBase.onFieldTypeChanged] 📊 Checking typeChangeSignal:',
      this.typeChangeSignal,
    );

    if (this.typeChangeSignal === null || this.typeChangeSignal === undefined) {
      this.log.error(
        '[FieldEditBase.onFieldTypeChanged] ❌ CRITICAL: typeChangeSignal is NULL/UNDEFINED!',
      );
      this.log.error(
        '[FieldEditBase.onFieldTypeChanged] ❌ Type change will NOT be communicated to parent!',
      );
    } else {
      this.log.debug(
        '[FieldEditBase.onFieldTypeChanged] ✅ typeChangeSignal is defined, writing to signal...',
      );
      this.log.debug(
        '[FieldEditBase.onFieldTypeChanged] 📡 Writing to typeChangeSignal with data:',
        {
          newType,
          field,
        },
      );

      this.typeChangeSignal.set({ newType, field });

      this.log.debug('[FieldEditBase.onFieldTypeChanged] ✅ typeChangeSignal.set() completed');
      this.log.debug(
        '[FieldEditBase.onFieldTypeChanged] 📊 Verifying signal value:',
        this.typeChangeSignal(),
      );
    }

    this.log.debug('[FieldEditBase.onFieldTypeChanged] ===== EXIT =====');
  }

  /**
   * Set a validation error message
   * Used in saveChanges() to prevent navigation and signal validation failure
   *
   * @param message The error message to display
   *
   * @example
   * ```typescript
   * protected override saveChanges(): void {
   *   if (!this.field()?.label?.trim()) {
   *     this.setError('Label is required');
   *     return;
   *   }
   *   // Rest of save logic...
   * }
   * ```
   */
  protected setError(message: string): void {
    this.error.set(message);
  }

  /**
   * Clear any validation error message
   * Called automatically at the start of save()
   */
  protected clearError(): void {
    this.error.set(null);
  }

  /**
   * Get the current field with null check
   * Type-safe alternative to using field() directly
   *
   * @returns The current field or null if not loaded
   *
   * @example
   * ```typescript
   * const field = this.getField();
   * if (field) {
   *   field.label = 'New Label';
   * }
   * ```
   */
  protected getField(): FieldDefinition | null {
    return this.field();
  }

  /**
   * Get a specific property value from the current field
   * Type-safe property access with null check
   *
   * @param key The property name to retrieve (must be a key of FieldDefinition)
   * @returns The property value or undefined if field is null
   *
   * @example
   * ```typescript
   * const label = this.getFieldValue('label');
   * const required = this.getFieldValue('required');
   * ```
   */
  protected getFieldValue<K extends keyof FieldDefinition>(key: K): FieldDefinition[K] | undefined {
    const field = this.field();
    return field ? field[key] : undefined;
  }

  /**
   * Set a specific property value in the current field
   * Updates the field signal and triggers reactivity
   * Also syncs to parent's fieldSignal if provided (signal-based pattern)
   *
   * @param key The property name to update (must be a key of FieldDefinition)
   * @param value The new value for this property
   *
   * @example
   * ```typescript
   * this.setFieldValue('label', 'New Label');
   * this.setFieldValue('required', true);
   * ```
   */
  protected setFieldValue<K extends keyof FieldDefinition>(
    key: K,
    value: FieldDefinition[K],
  ): void {
    const field = this.field();
    if (field) {
      field[key] = value;
      // Use a single spread copy so both the local signal and the parent
      // fieldSignal point to the SAME object after this call.  Formly mutates
      // the model object in–place (via [model]="field()!"), so sharing the
      // reference means every keystroke the user makes is immediately visible
      // when the parent reads fieldSignal() on the Apply button click.
      const newField = { ...field };
      this.field.set(newField);
      // Sync to parent's fieldSignal if provided (signal-based pattern)
      if (this._fieldSignal) {
        this._fieldSignal.set(newField);
      }
    }
  }

  /**
   * Generate a valid field key from a label string.
   * Delegates to the standalone {@link generateKeyFromLabel} function.
   *
   * @param label The field label to convert (e.g., "First Name")
   * @returns Valid field key (e.g., "first-name")
   */
  protected generateKeyFromLabel(label: string): string {
    return generateKeyFromLabel(label);
  }

  /**
   * Validate that required field properties are not empty
   * Returns true if all specified fields are present and non-empty
   * Returns false and sets error if any are missing
   *
   * Used as a common validation pattern in saveChanges()
   * Automatically sets error message describing which fields are required
   *
   * @param requiredFields Array of field property names that must be non-empty
   * @returns true if all required fields have values, false otherwise
   *
   * @example
   * ```typescript
   * protected override saveChanges(): void {
   *   // Validates that 'label' is non-empty
   *   if (!this.validateRequired(['label'])) {
   *     return;  // Error message already set by validateRequired
   *   }
   *   // Continue with save logic
   * }
   * ```
   */
  protected validateRequired(...requiredFields: (keyof FieldDefinition)[]): boolean {
    const field = this.field();
    if (!field) {
      this.setError('Field data is missing');
      return false;
    }

    const missingFields: string[] = [];
    for (const fieldName of requiredFields) {
      const value = field[fieldName];
      // Check for empty string, null, undefined
      if (value === '' || value === null || value === undefined) {
        missingFields.push(String(fieldName));
      }
    }

    if (missingFields.length > 0) {
      this.setError(`Required fields are missing: ${missingFields.join(', ')}`);
      return false;
    }

    return true;
  }
}

/**
 * Generate a valid field key from a label string.
 *
 * Converts label to lowercase, replaces non-alphanumeric runs with hyphens,
 * and trims leading/trailing hyphens. Produces keys suitable for forms and APIs.
 *
 * This is an exported standalone function so it can be reused in Formly field
 * config hooks (field-config-forms.ts) and anywhere else outside FieldEditBase.
 *
 * @param label The field label to convert (e.g., "First Name")
 * @returns Valid field key (e.g., "first-name")
 *
 * @example
 * ```typescript
 * generateKeyFromLabel('First Name');     // 'first-name'
 * generateKeyFromLabel('E-mail Address'); // 'e-mail-address'
 * generateKeyFromLabel('User@Email!');    // 'useremail'
 * ```
 */
export function generateKeyFromLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
